import './list-content.scss';

import { Button, Empty, Pagination, Result, Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { getApiErrorMessage } from '@/api/errors';
import { LIST_PAGE_SIZES } from '@/shared/constants';
import type { IPageResult } from '@/shared/types/list.types';

interface ITexts {
  /** Заголовок ошибки загрузки. */
  error: string;
  emptyTitle: string;
  emptyDescription: string;
}

interface IProps<TItem> {
  data?: IPageResult<TItem>;
  loading: boolean;
  error?: unknown;
  onRetry: () => void;
  /** Задан ли поиск или фильтр — от этого зависит, какое пустое состояние показывать. */
  isFiltered: boolean;
  onResetFilters: () => void;
  texts: ITexts;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  /** Без пагинации — например, поиск клиента по ИИН отдаёт одну короткую выдачу. */
  hidePagination?: boolean;
  children: (items: TItem[], loading: boolean) => React.ReactNode;
}

/** Заголовок и пояснение пустого состояния. */
const EmptyText: React.FC<{ title: string; text: string }> = ({
  title,
  text
}) => (
  <span className="list-content__empty-text">
    <Typography.Text strong>{title}</Typography.Text>
    <Typography.Text type="secondary">{text}</Typography.Text>
  </span>
);

/**
 * Выдача списка: скелетон при первой загрузке, ошибка с повтором, два разных пустых
 * состояния (записей нет / поиск и фильтры ничего не нашли), содержимое и пагинация.
 * При перезапросе прошлая выдача остаётся на экране, пока не придёт новая.
 */
const ListContent = <TItem,>({
  data,
  loading,
  error,
  onRetry,
  isFiltered,
  onResetFilters,
  texts,
  pageSize,
  onPageChange,
  hidePagination,
  children
}: IProps<TItem>): React.ReactElement => {
  const { t } = useTranslation();

  if (!data && loading) {
    return (
      <Skeleton
        active
        paragraph={{ rows: 6 }}
        className="list-content__state"
      />
    );
  }

  if (!data) {
    return (
      <Result
        status="error"
        title={texts.error}
        subTitle={getApiErrorMessage(error)}
        extra={<Button onClick={onRetry}>{t('common.retry')}</Button>}
      />
    );
  }

  if (data.totalCount === 0) {
    return (
      <Empty
        className="list-content__state"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          isFiltered ? (
            <EmptyText
              title={t('common.list.noResults.title')}
              text={t('common.list.noResults.description')}
            />
          ) : (
            <EmptyText title={texts.emptyTitle} text={texts.emptyDescription} />
          )
        }
      >
        {isFiltered && (
          <Button onClick={onResetFilters}>
            {t('common.list.resetFilters')}
          </Button>
        )}
      </Empty>
    );
  }

  const from = (data.page - 1) * pageSize + 1;
  const to = from + data.items.length - 1;

  return (
    <div className="list-content">
      {children(data.items, loading)}

      {!hidePagination && (
        <Pagination
          className="list-content__pagination"
          current={data.page}
          pageSize={pageSize}
          total={data.totalCount}
          showSizeChanger
          pageSizeOptions={LIST_PAGE_SIZES}
          showTotal={(total) => t('common.list.range', { from, to, total })}
          responsive
          onChange={onPageChange}
        />
      )}
    </div>
  );
};

export default ListContent;
